import {
  Button as MuiButton,
  ClickAwayListener,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useCallback, useRef, useState } from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { SelectOption } from '../../../../model/selectOption'
import { Button } from '../../../../components/buttons'
import { TeamSprintGroup } from './sprintGroupTypes'
import { SprintGroupedCheckBoxPopper } from './SprintGroupedCheckBoxPopper'

const Container = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  height: '100%',
})

const SelectedChip = styled('div')({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px',
  flexWrap: 'wrap',
  background: colorPalette.monotone[1],
})

type Props = {
  value: string[] | undefined
  teamSprintGroups: TeamSprintGroup[]
  sprintOptions: SelectOption[]
  title: string
  onChange: (newValue: string[] | string | undefined) => void
}

export const SearchConditionSprintSelect = ({
  value,
  teamSprintGroups,
  sprintOptions,
  title,
  onChange,
}: Props) => {
  const [openPullDown, setOpenPullDown] = useState(false)
  const [pullDownAnchorEl, setPullDownAnchorEl] = useState<Element>()
  const openPullDownRef = useRef<HTMLSpanElement>(null)
  const [openExpandIconPullDown, setOpenExpandIconPullDown] = useState(false)
  const [expandIconAnchorEl, setExpandIconAnchorEl] = useState<Element>()
  const openExpandIconRef = useRef<HTMLSpanElement>(null)

  const handlePullDownClose = () => {
    setPullDownAnchorEl(undefined)
    setOpenPullDown(false)
  }

  const handleExpandIconPullDownClose = () => {
    setExpandIconAnchorEl(undefined)
    setOpenExpandIconPullDown(false)
  }

  const handlePopperChange = useCallback(
    (newValue: string[] | undefined) => {
      onChange(newValue)
    },
    [onChange]
  )

  const handleRemoveChip = useCallback(
    (uuid: string) => {
      const next = (value ?? []).filter(v => v !== uuid)
      onChange(next.length > 0 ? next : undefined)
    },
    [value, onChange]
  )

  const handleClearAll = useCallback(() => {
    onChange(undefined)
  }, [onChange])

  return (
    <Container>
      <Typography
        sx={{
          color: colorPalette.monotone[4],
          minWidth: '80px',
          marginRight: '10px',
        }}
      >
        {title}
      </Typography>
      <ClickAwayListener onClickAway={handlePullDownClose}>
        <span
          ref={openPullDownRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '416px',
          }}
        >
          {value && value.length >= 1 ? (
            <SelectedSprintChips
              value={value}
              sprintOptions={sprintOptions}
              onRemove={handleRemoveChip}
              onClearAll={handleClearAll}
            />
          ) : (
            <Button
              colorPattern="monotone"
              size="s"
              sx={{
                borderRadius: '15px',
                border: `1px dashed ${colorPalette.monotone[2]}`,
                background: colorPalette.monotone[0],
                color: colorPalette.monotone[3],
                margin: '4px auto 4px 15px',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (openPullDown) {
                  setPullDownAnchorEl(undefined)
                  setOpenPullDown(false)
                } else {
                  setPullDownAnchorEl(openPullDownRef.current ?? undefined)
                  setOpenPullDown(true)
                }
              }}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.click.select',
                })}
              </Typography>
              <AddRoundedIcon sx={{ height: '12px', width: '12px' }} />
            </Button>
          )}
          {openPullDown && (
            <SprintGroupedCheckBoxPopper
              open={openPullDown}
              anchorEl={pullDownAnchorEl}
              teamSprintGroups={teamSprintGroups}
              value={value}
              onChange={handlePopperChange}
              offset={20}
              placement="bottom-start"
            />
          )}
        </span>
      </ClickAwayListener>
      <ClickAwayListener onClickAway={handleExpandIconPullDownClose}>
        <span
          ref={openExpandIconRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '450px',
            margin: 'auto 0 auto auto',
          }}
        >
          <MuiButton
            sx={{ minWidth: '20px', margin: '0 0 0 auto' }}
            onClick={() => {
              if (openExpandIconPullDown) {
                setExpandIconAnchorEl(undefined)
                setOpenExpandIconPullDown(false)
              } else {
                setExpandIconAnchorEl(openExpandIconRef.current ?? undefined)
                setOpenExpandIconPullDown(true)
              }
            }}
          >
            {openExpandIconPullDown ? (
              <ExpandLess sx={{ color: colorPalette.monotone[4] }} />
            ) : (
              <ExpandMore sx={{ color: colorPalette.monotone[4] }} />
            )}
          </MuiButton>
          {openExpandIconPullDown && (
            <SprintGroupedCheckBoxPopper
              open={openExpandIconPullDown}
              anchorEl={expandIconAnchorEl}
              teamSprintGroups={teamSprintGroups}
              value={value}
              onChange={handlePopperChange}
              offset={18}
              placement="bottom-end"
            />
          )}
        </span>
      </ClickAwayListener>
    </Container>
  )
}

type SelectedSprintChipsProps = {
  value: string[]
  sprintOptions: SelectOption[]
  onRemove: (uuid: string) => void
  onClearAll: () => void
}

const SelectedSprintChips = ({
  value,
  sprintOptions,
  onRemove,
  onClearAll,
}: SelectedSprintChipsProps) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    }}
  >
    {value.map(uuid => {
      const option = sprintOptions.find(o => o.value === uuid)
      if (!option) return null
      return (
        <SelectedChip key={uuid}>
          <Typography
            sx={{
              margin: 'auto 5px',
              color: colorPalette.monotone[5],
            }}
          >
            {option.name}
          </Typography>
          <Button
            colorPattern="monotone"
            sx={{
              border: 'none',
              background: colorPalette.monotone[0],
              width: '10px',
              height: '24px',
              borderRadius: '15px',
              cursor: 'pointer',
            }}
            onClick={() => onRemove(uuid)}
          >
            <ClearRoundedIcon
              sx={{
                color: colorPalette.monotone[4],
                height: '13px',
                width: '13px',
              }}
            />
          </Button>
        </SelectedChip>
      )
    })}
    <MuiButton
      sx={{ minWidth: '20px', margin: '3px auto 3px 0' }}
      onClick={onClearAll}
    >
      <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
    </MuiButton>
  </div>
)
